/**
 * MergeTagsModal — merge one or more "source" tags into a "target" tag.
 * All customers of the source tags move to the target; the source tags are deleted.
 * Presentational: TagManager owns the selection and runs the merge (POST /api/tags/merge).
 *
 * Props:
 *   open, onOk, onCancel
 *   tags           all tags (with totalCustomers)
 *   sources        selected source tag objects
 *   target         selected target tag object or null
 *   onSources(tags) / onTarget(tag)
 */
import React from 'react';
import { WarningOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { Modal, Select, Field } from '../../ui';
import classes from './TagManager.css';

const tagOption = (t) => ({ value: t.id, label: t.name, color: t.color, count: t.totalCustomers });

const renderOption = (opt) => (
  <span className={classes.optionWithDot}>
    <span className={classes.dot} style={{ background: opt.data.color }} />
    <span className={classes.optionName}>{opt.label}</span>
    <span className={`${classes.optionCount} ui-tabular`}>{opt.data.count} לקוחות</span>
  </span>
);

const MergeTagsModal = ({ open, onOk, onCancel, tags, sources, target, onSources, onTarget }) => {
  const sourceIds = sources.map((t) => t.id);
  const movedCount = sources.reduce((sum, t) => sum + (t.totalCustomers || 0), 0);

  return (
    <Modal
      open={open}
      title="מיזוג טאגים"
      okText={sources.length ? `מזג ${sources.length} טאג/ים` : 'מזג'}
      okButtonProps={{ danger: true, disabled: sources.length === 0 || !target }}
      cancelText="ביטול"
      onOk={onOk}
      onCancel={onCancel}
      width={620}
      destroyOnHidden
    >
      <p className={classes.mergeIntro}>
        כל הלקוחות מטאגי המקור יועברו לטאג היעד, וטאגי המקור יימחקו.
      </p>

      <div className={classes.mergeGrid}>
        <Field label="טאגי מקור (יימחקו)">
          <Select
            mode="multiple"
            allowClear
            optionFilterProp="label"
            placeholder="בחר טאג אחד או יותר"
            value={sourceIds}
            onChange={(ids) => onSources(tags.filter((t) => ids.includes(t.id)))}
            options={tags.filter((t) => !target || t.id !== target.id).map(tagOption)}
            optionRender={renderOption}
          />
        </Field>
        <ArrowLeftOutlined className={classes.mergeArrow} />
        <Field label="טאג יעד (יישאר)">
          <Select
            showSearch
            allowClear
            optionFilterProp="label"
            placeholder="בחר טאג יעד"
            value={target ? target.id : undefined}
            onChange={(id) => onTarget(id ? tags.find((t) => t.id === id) : null)}
            options={tags.filter((t) => !sourceIds.includes(t.id)).map(tagOption)}
            optionRender={renderOption}
          />
        </Field>
      </div>

      {sources.length > 0 && target ? (
        <div className={classes.mergeWarning}>
          <WarningOutlined />
          <span>
            {movedCount} לקוחות יועברו ל-<strong>{target.name}</strong> ו-{sources.length} טאג/ים יימחקו. אי אפשר לבטל פעולה זו.
          </span>
        </div>
      ) : null}
    </Modal>
  );
};

export default MergeTagsModal;
